import { Helmet } from 'react-helmet-async';
import { SITE_BRANDS, currentSiteId } from '@/config/siteBrands';

const SITE_META = {
  auto: {
    siteName: 'CarsbikesPrices',
    defaultTitle: 'CarsbikesPrices - Compare Cars, Bikes & Scooter Prices in India',
    defaultDescription: 'Find and compare the latest prices for cars, bikes, and scooters in India. Get on-road prices, specs, reviews, and expert comparisons.',
    defaultKeywords: 'car prices India, bike prices India, scooter prices, compare vehicles, on-road price, best cars 2026, best bikes 2026',
    themeColor: '#dc2626',
    favicon: '/favicon-auto',
    domain: 'https://carsbikesprices.com',
    twitterHandle: '@carsbikesprices',
    locale: 'en_IN',
  },
  coupon: {
    siteName: 'MetroMandi',
    defaultTitle: 'MetroMandi - Best Coupons, Deals & Offers in India',
    defaultDescription: 'Save big with verified coupons, cashback offers, and live deals from top Indian brands. Flipkart, Amazon, Myntra, Swiggy and more.',
    defaultKeywords: 'coupons India, deals, offers, cashback, promo codes, discount codes, Flipkart coupons, Amazon deals, online shopping deals',
    themeColor: '#7c3aed',
    favicon: '/favicon-coupon',
    domain: 'https://metromandi.com',
    twitterHandle: '@metromandi',
    locale: 'en_IN',
  },
  admin: {
    siteName: 'AllMarketingMixed',
    defaultTitle: 'AllMarketingMixed - Digital Marketing & Content Platform',
    defaultDescription: 'AllMarketingMixed - Admin platform for CarsbikesPrices and MetroMandi. Manage vehicles, coupons, ads, affiliates and analytics.',
    defaultKeywords: 'allmarketingmixed, digital marketing, content management',
    themeColor: '#111111',
    favicon: '/favicon-admin',
    domain: 'https://allmarketingmixed.com',
    twitterHandle: '@allmarketingmxd',
    locale: 'en_IN',
  },
  finance: {
    siteName: 'YourMoneyWise',
    defaultTitle: 'YourMoneyWise - Smart Financial Tools & Comparisons',
    defaultDescription: 'Free SIP, EMI, FD, Tax calculators. Compare credit cards, demat accounts, insurance. Learn investing basics. India\'s trusted financial education platform.',
    defaultKeywords: 'SIP calculator, EMI calculator, credit card comparison, demat account, mutual funds India, tax savings, financial literacy',
    themeColor: '#059669',
    favicon: '/favicon-finance',
    domain: 'https://yourmoneywise.net',
    twitterHandle: '@yourmoneywise',
    locale: 'en_IN',
  },
  dgtg: {
    siteName: 'DGTG',
    defaultTitle: 'DGTG - Digital Growth & Tech Guides',
    defaultDescription: 'DGTG - practical digital growth, tech and tools guides.',
    defaultKeywords: 'digital growth, tech guides, dgtg',
    themeColor: '#0ea5e9',
    favicon: '/favicon-dgtg',
    domain: 'https://dgtg.in',
    twitterHandle: '@dgtg',
    locale: 'en_IN',
  },
  games: {
    siteName: 'GamesNFans',
    defaultTitle: 'GamesNFans - Games, Esports, Cricket & Fan Gear',
    defaultDescription: 'GamesNFans - read, shop, play and create for gamers and sports fans.',
    defaultKeywords: 'gaming news, esports, cricket, fan gear',
    themeColor: '#f59e0b',
    favicon: '/favicon-games',
    domain: 'https://gamesnfans.tv',
    twitterHandle: '@gamesnfans',
    locale: 'en_IN',
  },
  vikas: {
    siteName: 'Vikasifications',
    defaultTitle: 'Vikasifications',
    defaultDescription: 'Vikasifications.',
    defaultKeywords: 'vikasifications',
    themeColor: '#111111',
    favicon: '/favicon-vikas',
    domain: 'https://vikasifications.com',
    twitterHandle: '@vikasifications',
    locale: 'en_IN',
  },
  devmob: {
    siteName: 'DevMob',
    defaultTitle: 'DevMob - Mobile Dev Guides & Tools',
    defaultDescription: 'DevMob - mobile development guides, tools and resources.',
    defaultKeywords: 'mobile development, app dev, devmob',
    themeColor: '#8b5cf6',
    favicon: '/favicon-devmob',
    domain: 'https://devmob.co',
    twitterHandle: '@devmob',
    locale: 'en_IN',
  },
  travel: {
    siteName: 'WokeTraveler',
    defaultTitle: 'WokeTraveler - Travel Guides & Itineraries',
    defaultDescription: 'WokeTraveler - travel guides, itineraries and destination tips.',
    defaultKeywords: 'travel guides, itineraries, destinations',
    themeColor: '#0d9488',
    favicon: '/favicon-travel',
    domain: 'https://woketraveler.com',
    twitterHandle: '@woketraveler',
    locale: 'en_IN',
  },
  tech: {
    siteName: 'Tech-N-AI Talks',
    defaultTitle: 'Tech-N-AI Talks - AI Tools & Software Reviews',
    defaultDescription: 'Tech-N-AI Talks - AI tools, software reviews and tech news.',
    defaultKeywords: 'AI tools, software reviews, tech news',
    themeColor: '#6366f1',
    favicon: '/favicon-tech',
    domain: 'https://tech-n-ai-talks.com',
    twitterHandle: '@technaitalks',
    locale: 'en_IN',
  },
};

// Resolves the effective site id in preview "all" builds via the same
// sessionStorage breadcrumb DynamicFavicon (App.js) already maintains —
// otherwise every page without an explicit `site` prop defaults to 'auto'.
function resolvedSiteId(explicitSite) {
  return currentSiteId(explicitSite);
}

export default function SEO({ title, description, keywords, canonical, noindex, type = 'website', image, site }) {
  const siteId = resolvedSiteId(site);
  const brand = SITE_BRANDS[siteId] || SITE_BRANDS.auto;
  // Canonical origin + share image come from the site registry, so every page on a site uses that
  // site's own domain and artwork (never the shared AllMarketingMixed og-image.jpg).
  const meta = { ...(SITE_META[siteId] || SITE_META.auto), domain: brand.origin };

  const finalTitle = title ? `${title} | ${meta.siteName}` : meta.defaultTitle;
  const finalDesc = description || meta.defaultDescription;
  const finalKeywords = keywords || meta.defaultKeywords;
  const finalImage = image || `${brand.origin}${brand.og}`;
  const finalCanonical = canonical || (typeof window !== 'undefined' ? window.location.href : '');

  return (
    <Helmet>
      {/* Core */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="author" content={meta.siteName} />
      <meta name="theme-color" content={meta.themeColor} />
      <meta name="robots" content={noindex ? 'noindex,nofollow' : 'index,follow'} />
      {finalCanonical && <link rel="canonical" href={finalCanonical} />}

      {/* Favicon, dynamic per site */}
      <link rel="icon" type="image/png" sizes="32x32" href={`${meta.favicon}-32.png`} />
      <link rel="icon" type="image/png" sizes="192x192" href={`${meta.favicon}-192.png`} />
      <link rel="apple-touch-icon" sizes="180x180" href={`${meta.favicon}-180.png`} />

      {/* Open Graph */}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDesc} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={meta.siteName} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:locale" content={meta.locale} />
      {finalCanonical && <meta property="og:url" content={finalCanonical} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />
      <meta name="twitter:image" content={finalImage} />
      <meta name="twitter:site" content={meta.twitterHandle} />

      {/* Geo */}
      <meta name="geo.region" content="IN" />
      <meta name="geo.placename" content="India" />
      <meta name="language" content="English" />
    </Helmet>
  );
}
